import React from 'react';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { FlexSpace } from 'components/FlexSpace';
import { CenteredError, CenteredLoader } from 'components/Loaders';
import { Modal } from 'components/Modal';
import { SearchInput } from 'components/SearchInput';

import { useCoreApi } from 'hooks/useApi';

import { formatDateWithTimezone } from 'utils/formatting';

import { MinMaxChangesTable } from './MinMaxChangesTable';
import { getAdjacentRunIds, getMinMaxChanges, sortByLargestChange } from './utils';

/**
 * Every item-location an automated run exported, with what each min/max changed from.
 *
 * Reached from the run list and from the last-run card, so there is one rendering of the change list.
 */
export function AutomatedExportRunModal({
  jobId,
  runIds,
  onNavigate,
  onClose,
}: {
  jobId: string;
  /** The loaded runs, newest first, so the modal can step between them without its own request. */
  runIds: string[];
  onNavigate: (runId: string) => void;
  onClose: () => void;
}) {
  const { data: asyncJob, isLoading, error } = useCoreApi(schemas.asyncJobs.getAsyncJob, { pathParams: { jobId } });
  const [searchQuery, setSearchQuery] = React.useState('');

  const changes = React.useMemo(() => sortByLargestChange(getMinMaxChanges(asyncJob?.steps ?? [])), [asyncJob]);
  const filteredChanges = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return changes;
    return changes.filter(
      (change) => change.itemId.toLowerCase().includes(query) || change.locationId.toLowerCase().includes(query),
    );
  }, [changes, searchQuery]);

  const notExportedCount = changes.filter((change) => !change.exported).length;

  const { previousRunId, nextRunId } = getAdjacentRunIds(runIds, jobId);

  return (
    <Modal
      visible
      width={900}
      title={`Automated export ${asyncJob?.createdAt ? `- ${formatDateWithTimezone(asyncJob.createdAt)}` : ''}`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <FlexSpace
        justify="space-between"
        fullWidth
        className={css`
          margin-bottom: 12px;
        `}
      >
        <Button
          size="small"
          disabled={!nextRunId}
          onClick={() => nextRunId && onNavigate(nextRunId)}
          icon={<LeftOutlined />}
        >
          Next
        </Button>
        <Button size="small" disabled={!previousRunId} onClick={() => previousRunId && onNavigate(previousRunId)}>
          Previous <RightOutlined />
        </Button>
      </FlexSpace>
      {error ? (
        <CenteredError error={error} />
      ) : isLoading ? (
        <CenteredLoader />
      ) : (
        <>
          {notExportedCount > 0 && (
            <Alert
              message={`${notExportedCount} of ${changes.length} item-locations failed to export, so their values were not sent to the ERP.`}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <SearchInput
            placeholder="Search item or location"
            query={searchQuery}
            onQueryChange={setSearchQuery}
            className={css`
              margin-bottom: 12px;
            `}
          />
          <MinMaxChangesTable changes={filteredChanges} pageSize={20} />
        </>
      )}
    </Modal>
  );
}
